import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, createStyles } from '@mantine/core';
import ClassicLoadingSpinner from './LoadingSpinner/ClassicLoadingSpinner';

type Option = { id: string; label: string };

const useStyles = createStyles((theme) => ({
  option: {
    cursor: 'pointer',
    padding: '0.5rem',
    fontSize: '0.8rem',
    borderBottom: '1px solid #eee',
    backgroundColor: '#fff',

    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
}));

export default function AutocompletePopover(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: Option[];
  onOptionClick: (option: Option) => void;
  opened?: boolean;
  loading?: boolean;
  inputClassName?: string;
  maxDropdownHeight?: string;
  clearOnSelect?: boolean;
}) {
  const {
    value,
    onChange,
    placeholder,
    options,
    onOptionClick,
    opened,
    loading,
    inputClassName = 'form-control',
    maxDropdownHeight = '4rem',
    clearOnSelect = false,
  } = props;

  const { classes } = useStyles();
  const inputRef = useRef<any>(null);
  const [dropdownWidth, setDropdownWidth] = useState('auto');

  useEffect(() => {
    if (inputRef.current != null) {
      setDropdownWidth(`${inputRef.current.offsetWidth}px`);
    }
  }, [inputRef, value]);

  const isOpen = useMemo(() => {
    if (typeof opened === 'boolean') return opened;
    return Boolean(loading) || options.length > 0;
  }, [opened, loading, options.length]);

  return (
    <Popover opened={isOpen} position="bottom" withArrow={false}>
      <Popover.Target>
        <input
          ref={inputRef}
          type="text"
          className={inputClassName}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      </Popover.Target>
      <Popover.Dropdown
        style={{
          overflowY: 'auto',
          maxHeight: maxDropdownHeight,
          border: '1px solid #ccc',
          padding: '0',
          zIndex: 100,
        }}
      >
        {loading && (
          <div
            style={{
              width: dropdownWidth,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <ClassicLoadingSpinner />
          </div>
        )}

        {!loading &&
          options.map((opt, index) => (
            <option
              className={classes.option}
              style={{
                width: dropdownWidth,
                borderBottom:
                  index === options.length - 1 ? 'none' : '1px solid #eee',
              }}
              onClick={() => {
                onOptionClick(opt);
                if (clearOnSelect) onChange('');
              }}
              key={opt.id}
              value={opt.id}
              label={opt.label}
            />
          ))}
      </Popover.Dropdown>
    </Popover>
  );
}
